import { ReactNode, useState } from 'react'
import { Link } from 'react-router-dom'
import { useLang, t, fmt, demoText, sexLabel, tierShortLabel } from '../lib/i18n'
import {
  AlertTriangle,
  BadgeCheck,
  Calendar,
  Check,
  ChevronRight,
  Copy,
  Stethoscope,
  FileText,
  Plus,
  ShieldCheck,
  Sparkles,
  Info,
  Activity,
  HeartPulse,
  Droplets,
  IdCard,
  MapPin,
  Phone,
  Ruler,
  Weight,
  X,
} from 'lucide-react'

type Allergy = {
  id: string
  name: string
  severity: 'mild' | 'moderate' | 'severe' | 'life-threatening'
  type: 'drug' | 'food' | 'environmental'
}

type Vital = {
  id: string
  label: string
  value: string
  unit: string | null
  date: string
  trend: string
}

type AdvanceDirective = {
  id: string
  type: string
  date: string
  status: 'active' | 'draft'
  summary: string
}

type ClinicalNote = {
  id: string
  date: string
  provider: string
  text: string
}

type PatientBannerProps = {
  person: {
    name_ar: string
    name_latin: string
    nhi: string
    mrn: string
    facility: string
    dob: string
    sex: string
    phone: string
    address: {
      governorate: string
      city: string
    }
    verificationTier: 1 | 2 | 3 | 4
  }
  allergies: Allergy[]
  vitals: Vital[]
  advanceDirectives: AdvanceDirective[]
  latestNote: ClinicalNote | null
  onAddNote: () => void
  onViewAllNotes: () => void
}

const tierIcons = [null, <ShieldCheck className="h-4 w-4" />, <ShieldCheck className="h-4 w-4" />, <ShieldCheck className="h-4 w-4" />, <ShieldCheck className="h-4 w-4" />] as const
// Tier labels come from the shared VERIFICATION_TIERS source of truth
// (lib/verificationTiers.ts) — see verificationTierShort(). Localized names
// and per-level descriptions come from the i18n dict (tier.name{n}/.desc{n}).
function AssuranceTierGrid({ person }: { person: PatientBannerProps['person'] }) {
  const tiers = [1, 2, 3, 4].map((level) => ({
    level,
    name: t(`tier.name${level}`),
    desc: t(`tier.desc${level}`),
    tone:
      level === person.verificationTier
        ? 'border-emerald-300 bg-emerald-50 text-emerald-900 ring-1 ring-emerald-200'
        : level === 1
          ? 'border-stone-200 bg-stone-50 text-stone-600'
          : level === 2
            ? 'border-sky-200 bg-sky-50 text-sky-700'
            : 'border-emerald-200 bg-emerald-50 text-emerald-700',
  }))
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {tiers.map((tier) => (
        <div key={tier.level} className={`flex items-start gap-3 rounded-lg border p-3 ${tier.tone}`}>
          <div className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-white/70">
            {tierIcons[tier.level]}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold">
              <span>L{tier.level}</span> <span>{tier.name}</span>
            </div>
            <p className="mt-0.5 text-xs leading-5 opacity-80">{tier.desc}</p>
          </div>
        </div>
      ))}
    </div>
  )
}

function age(dob: string): string {
  const birth = new Date(dob)
  const now = new Date()
  const years = now.getFullYear() - birth.getFullYear()
  const months = now.getMonth() - birth.getMonth()
  const value = months < 0 || (months === 0 && now.getDate() < birth.getDate()) ? years - 1 : years
  return fmt(t('banner.ageYears'), { n: value })
}

type IdentityDetailsOpenProps = {
  person: PatientBannerProps['person']
  advanceDirectives: AdvanceDirective[]
  onViewAllNotes: () => void
}

/**
 * Verification + assurance detail — deliberately NOT in the main flow.
 * Reachable only through the "Identity details" disclosure, so the patient
 * sees name/NHI/DOB/sex first and the registrar-facing verification model
 * stays one click away without shouting.
 */
function IdentityDetailsContent({ person, advanceDirectives, onViewAllNotes }: IdentityDetailsOpenProps) {
  return (
    <div className="space-y-5">
      {/* Assurance model — the L1–L4 explainer moved off the main dashboard */}
      <div>
        <div
          className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-stone-500"
          data-testid="assurance-model-heading"
        >
          <Info className="h-3.5 w-3.5" aria-hidden="true" />
          {t('dash.assuranceModel')}
        </div>
        <AssuranceTierGrid person={person} />
      </div>

      {/* Contact + identifiers — confirm-"this is me" details, not daily use */}
      <div className="grid gap-3 text-sm text-stone-600 sm:grid-cols-2">
        <p className="inline-flex items-center gap-2">
          <Phone className="h-3.5 w-3.5 shrink-0 text-stone-400" aria-hidden="true" />
          <span className="font-mono">{person.phone}</span>
        </p>
        <p className="inline-flex items-center gap-2">
          <MapPin className="h-3.5 w-3.5 shrink-0 text-stone-400" aria-hidden="true" />
          <span>{[person.address.city, person.address.governorate].filter(Boolean).map((part) => demoText(part)).join(', ')}</span>
        </p>
        <p className="inline-flex items-center gap-2">
          <IdCard className="h-3.5 w-3.5 shrink-0 text-stone-400" aria-hidden="true" />
          <span>
            <span className="text-[10px] font-semibold uppercase tracking-wider text-stone-400">MRN</span>{' '}
            <span className="font-mono text-stone-700">{person.mrn}</span>
            <span className="ms-2 text-xs text-stone-400">{demoText(person.facility)}</span>
          </span>
        </p>
      </div>

      {/* Advance directives — important, but not part of confirming identity */}
      <div>
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-stone-800">
          {t('banner.directives')}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {advanceDirectives.map(dir => (
            <Link
              key={dir.id}
              to="/advance-directives"
              title={fmt(t('banner.viewDirective'), { type: dir.type })}
              className="inline-flex items-center gap-1.5 rounded-lg border border-stone-100 bg-stone-50 px-2.5 py-1 text-xs font-medium text-stone-700 transition hover:border-emerald-200 hover:bg-white hover:shadow-sm active:scale-95"
            >
              <FileText className="h-3.5 w-3.5 text-stone-400" aria-hidden="true" />
              <span>{dir.type}</span>
              <span className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                dir.status === 'active' ? 'bg-emerald-100 text-emerald-900' : 'bg-amber-100 text-amber-900'
              }`}>
                {dir.status === 'active' ? `✓ ${t('banner.activeOk')}` : `… ${t('banner.draft')}`}
              </span>
            </Link>
          ))}
          {advanceDirectives.length === 0 && (
            <span className="text-sm italic text-stone-500">{t('banner.noneOnFile')}</span>
          )}
          <Link
            to="/advance-directives"
            className="ms-auto inline-flex items-center gap-0.5 text-xs font-medium text-emerald-800 hover:text-emerald-900"
          >
            {t('banner.manage')} <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </div>
  )
}

/**
 * Slim patient banner — identity row (name/NHI/DOB/sex) + allergies + vitals
 * chip strip + latest note. Verification tier, MRN/facility, contact details
 * and the assurance-model grid all live behind the "Identity details"
 * disclosure, so the first screen answers "this is me" and nothing else.
 */
export function PatientBanner({
  person,
  allergies,
  vitals,
  advanceDirectives,
  latestNote,
  onAddNote,
  onViewAllNotes,
}: PatientBannerProps) {
  const [showNoteForm, setShowNoteForm] = useState(false)
  const [noteText, setNoteText] = useState('')
  const [copied, setCopied] = useState(false)
  const [showIdentity, setShowIdentity] = useState(false)

  const criticalAllergies = allergies.filter(a => a.severity === 'severe' || a.severity === 'life-threatening')
  const hasCriticalAllergy = criticalAllergies.length > 0

  const keyVitals = vitals.slice(0, 5)
  const vitalIcons: Record<string, ReactNode> = {
    height: <Ruler className="h-3.5 w-3.5" />,
    weight: <Weight className="h-3.5 w-3.5" />,
    bmi: <Activity className="h-3.5 w-3.5" />,
    bp: <HeartPulse className="h-3.5 w-3.5" />,
    hba1c: <Droplets className="h-3.5 w-3.5" />,
  }

  function copyNhi() {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(person.nhi).catch(() => {})
    }
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1800)
  }

  return (
    <section className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-lg shadow-stone-900/5">
      {/* ===== Identity row: avatar | name + the confirm-"this is me" basics ===== */}
      <div className="border-b border-stone-100 px-5 py-5 sm:px-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          {/* Avatar + name + core demographics */}
          <div className="flex min-w-0 items-start gap-4">
            <div className="relative shrink-0">
              <div className="h-[72px] w-[72px] overflow-hidden rounded-xl ring-2 ring-emerald-200 ring-offset-2 ring-offset-white">
                <img
                  src="/avatars/ahmad-al-hassan.png"
                  alt={t('banner.photoAlt')}
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              </div>
              {/* Verified badge */}
              <span
                className="absolute -bottom-1.5 -right-1.5 grid h-7 w-7 place-items-center rounded-full bg-emerald-600 text-white shadow-md ring-2 ring-white"
                title={t('banner.verifiedTitle')}
                aria-label={t('banner.verifiedAria')}
              >
                <BadgeCheck className="h-4 w-4" aria-hidden="true" />
              </span>
            </div>

            <div className="min-w-0">
              <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5">
                <h2 lang="ar" className="font-[Noto_Naskh_Arabic,serif] text-2xl font-bold leading-tight text-stone-950">
                  {person.name_ar}
                </h2>
                <span className="text-lg text-stone-300">/</span>
                <p className="text-xl font-semibold text-stone-700">{person.name_latin}</p>
              </div>

              {/* The basics — exactly what confirms "this is me" */}
              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm text-stone-600">
                <span className="inline-flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5 text-stone-400" aria-hidden="true" />
                  <span className="font-medium text-stone-800">{person.dob}</span>
                  <span className="rounded-md bg-stone-100 px-1.5 py-0.5 text-xs font-semibold text-stone-700">
                    {age(person.dob)}
                  </span>
                </span>
                <span className="text-stone-300" aria-hidden="true">|</span>
                <span>{person.sex ? sexLabel(person.sex) : '—'}</span>
                <span className="text-stone-300" aria-hidden="true">|</span>
                <span className="inline-flex items-center gap-1.5">
                  <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" aria-hidden="true" />
                  <span>{fmt(t('banner.level'), { n: person.verificationTier, name: tierShortLabel(person.verificationTier) })}</span>
                </span>
              </div>
            </div>
          </div>

          {/* NHI + actions (right) */}
          <div className="flex shrink-0 flex-col items-start gap-2 lg:items-end">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-stone-400">NHI</span>
              <button
                type="button"
                onClick={copyNhi}
                title={t('banner.copyNhiAria')}
                aria-label={t('banner.copyNhiAria')}
                className="group inline-flex items-center gap-1.5 rounded-lg border border-stone-200 bg-stone-50 px-2.5 py-1.5 font-mono text-sm font-semibold text-emerald-800 transition hover:border-emerald-300 hover:bg-white active:scale-95"
              >
                {person.nhi}
                {copied ? (
                  <Check className="h-3.5 w-3.5 animate-check-pop text-emerald-600" aria-hidden="true" />
                ) : (
                  <Copy className="h-3.5 w-3.5 text-stone-400 transition group-hover:text-emerald-600" aria-hidden="true" />
                )}
              </button>
            </div>

            {/* Identity details disclosure — verification/assurance tucked away */}
            <button
              type="button"
              onClick={() => setShowIdentity((open) => !open)}
              aria-expanded={showIdentity}
              data-testid="identity-details-toggle"
              className="inline-flex items-center gap-1.5 rounded-lg border border-stone-200 px-3 py-1.5 text-sm font-medium text-stone-600 transition hover:border-emerald-200 hover:bg-stone-50 hover:text-emerald-900"
            >
              <Info className="h-3.5 w-3.5 text-stone-400" aria-hidden="true" />
              {t('dash.identityDetails')}
            </button>

            {/* Actions */}
            <div className="mt-1 flex gap-2">
              <button type="button" onClick={onAddNote} className="inline-flex items-center gap-1.5 rounded-lg bg-[#143f3a] px-3 py-1.5 text-sm font-medium text-white hover:bg-[#0f302c] transition">
                <Plus className="h-4 w-4" aria-hidden="true" /> {t('banner.noteBtn')}
              </button>
              <button type="button" onClick={onViewAllNotes} className="inline-flex items-center gap-1.5 rounded-lg border border-stone-200 px-3 py-1.5 text-sm font-medium text-stone-700 hover:bg-stone-50 transition">
                <FileText className="h-4 w-4" aria-hidden="true" /> {t('banner.notesBtn')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ===== Identity details panel (verification tier, MRN, directives…) ===== */}
      {showIdentity && (
        <div
          className="border-b border-stone-100 bg-stone-50/70 px-5 py-4 sm:px-6"
          data-testid="identity-details-panel"
        >
          <IdentityDetailsContent
            person={person}
            advanceDirectives={advanceDirectives}
            onViewAllNotes={onViewAllNotes}
          />
        </div>
      )}

      {/* ===== Warning strip: allergies ===== */}
      <div className={`border-b border-stone-100 px-5 py-3 sm:px-6 ${hasCriticalAllergy ? 'bg-rose-50/60' : ''}`}>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-stone-800">
            <AlertTriangle className={`h-4 w-4 ${hasCriticalAllergy ? 'text-rose-700' : 'text-stone-400'}`} aria-hidden="true" />
            {t('banner.allergies')}
          </span>
          <div className="flex flex-wrap items-center gap-2">
            {allergies.map(allergy => (
              <Link
                key={allergy.id}
                to="/allergies"
                title={fmt(t('banner.viewAllergy'), { name: demoText(allergy.name) })}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition hover:shadow-sm active:scale-95 ${
                  allergy.severity === 'life-threatening' || allergy.severity === 'severe'
                    ? 'border-rose-300 bg-rose-100 text-rose-900 hover:border-rose-400'
                    : allergy.severity === 'moderate'
                      ? 'border-amber-200 bg-amber-50 text-amber-900 hover:border-amber-300'
                      : 'border-blue-200 bg-blue-50 text-blue-900 hover:border-blue-300'
                }`}
              >
                <span aria-hidden="true">{allergy.severity === 'life-threatening' ? '🔴' : allergy.severity === 'severe' ? '🟠' : allergy.severity === 'moderate' ? '🟡' : '🔵'}</span>
                <span>{demoText(allergy.name)}</span>
                <span className="opacity-60">({t(`banner.allergyType.${allergy.type}`)})</span>
              </Link>
            ))}
            {allergies.length === 0 && <span className="text-sm italic text-stone-500">{t('banner.noAllergies')}</span>}
          </div>
          {hasCriticalAllergy && (
            <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-xs font-medium text-rose-900">
              {t('banner.reviewBeforePrescribing')}
            </span>
          )}
        </div>
      </div>

      {/* ===== Vitals inline chip strip ===== */}
      <div className="border-b border-stone-100 px-5 py-3.5 sm:px-6">
        <div className="flex flex-wrap items-center gap-2">
          <span className="mr-1 inline-flex items-center gap-1.5 text-sm font-semibold text-stone-800">
            <Stethoscope className="h-4 w-4 text-stone-400" aria-hidden="true" />
            {t('banner.vitals')}
          </span>
          <div className="flex flex-1 flex-wrap items-center gap-2">
            {keyVitals.map(vital => (
              <VitalChip
                key={vital.id}
                icon={vitalIcons[vital.id] ?? <Activity className="h-3.5 w-3.5" />}
                label={vital.label}
                value={vital.value}
                unit={vital.unit}
              />
            ))}
            {vitals.length > 5 && (
              <span className="text-xs font-medium text-stone-400">{fmt(t('banner.moreVitals'), { n: vitals.length - 5 })}</span>
            )}
          </div>
        </div>
      </div>

      {/* ===== Latest clinical note (single line) ===== */}
      {latestNote && (
        <div className="bg-stone-50/60 px-5 py-3 sm:px-6">
          <div className="flex items-center gap-3">
            <div className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-emerald-100 text-emerald-900">
              <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-xs text-stone-500">
                <span className="font-semibold text-stone-700">{t('banner.latestNote')}</span>
                <span aria-hidden="true">·</span>
                <span>{latestNote.provider}</span>
                <span aria-hidden="true">·</span>
                <span>{latestNote.date}</span>
              </div>
              <p className="truncate text-sm text-stone-700">{demoText(latestNote.text)}</p>
            </div>
            <button type="button" onClick={onViewAllNotes} className="shrink-0 text-sm font-medium text-emerald-800 hover:text-emerald-900">
              {t('banner.viewAll')}
            </button>
          </div>
        </div>
      )}

      {/* ===== Note form ===== */}
      {showNoteForm && (
        <div className="border-t border-stone-100 px-5 py-4 sm:px-6">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-medium text-stone-700">{t('banner.addNote')}</h3>
            <button type="button" onClick={() => setShowNoteForm(false)} aria-label={t('banner.close')} className="grid h-7 w-7 place-items-center rounded-lg text-stone-400 hover:text-stone-600">
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
          <textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder={t('banner.notePlaceholder')}
            rows={3}
            className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 outline-none"
          />
          <div className="mt-3 flex gap-2">
            <button type="button" className="inline-flex items-center gap-1.5 rounded-lg bg-[#143f3a] px-3 py-1.5 text-sm font-medium text-white hover:bg-[#0f302c]">
              {t('banner.saveNote')}
            </button>
            <button type="button" onClick={() => setShowNoteForm(false)} className="rounded-lg border border-stone-200 px-3 py-1.5 text-sm font-medium text-stone-700 hover:bg-stone-50">
              {t('reg.cancel')}
            </button>
          </div>
        </div>
      )}
    </section>
  )
}

/* Small icon + label vitals chip */
function VitalChip({ icon, label, value, unit }: { icon: ReactNode; label: string; value: string; unit: string | null }) {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-stone-100 bg-stone-50/70 px-2.5 py-1.5">
      <span className="grid h-6 w-6 shrink-0 place-items-center rounded-md bg-white text-emerald-800 ring-1 ring-stone-100">
        {icon}
      </span>
      <div className="min-w-0 leading-tight">
        <div className="text-[10px] font-medium uppercase tracking-wide text-stone-500">{label}</div>
        <div className="text-sm font-semibold text-stone-950">
          {value}
          {unit && <span className="ml-0.5 text-[11px] font-normal text-stone-500">{unit}</span>}
        </div>
      </div>
    </div>
  )
}